import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { CheckCircle, Clock, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format pickup time - handles both "HH:MM" and ISO formats
const formatPickupTime = (pickupSlot, pickupTime, createdAt) => {
  const timeValue = pickupSlot || pickupTime;
  if (!timeValue) return 'À définir';
  
  // Check if it's just a time format (HH:MM)
  if (/^\d{1,2}:\d{2}$/.test(timeValue)) {
    return `Aujourd'hui à ${timeValue}`;
  }
  
  // Try to parse as a full date
  const date = new Date(timeValue);
  if (!isNaN(date.getTime())) {
    return date.toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  // Fallback: return the raw value or "À définir"
  return timeValue || 'À définir';
};

export default function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useAuth();
  const { refreshCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    let isMounted = true;
    
    if (!sessionId) {
      // Use setTimeout to avoid synchronous setState in effect
      setTimeout(() => {
        if (isMounted) setLoading(false);
      }, 0);
      return () => { isMounted = false; };
    }

    const maxAttempts = 5;
    const pollInterval = 2000;

    const checkPaymentStatus = async (attempts = 0) => {
      if (!isMounted) return;
      
      if (attempts >= maxAttempts) {
        toast.error('Impossible de vérifier le paiement. Veuillez contacter le support.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);

        if (!isMounted) return;

        if (response.data.payment_status === 'paid') {
          toast.success('Paiement confirmé!');
          refreshCart();
          const ordersResponse = await axios.get(`${API}/orders`);
          if (ordersResponse.data.length > 0) {
            setOrder(ordersResponse.data[0]);
          }
          setLoading(false);
        } else if (response.data.status === 'expired') {
          toast.error('Session de paiement expirée');
          setLoading(false);
        } else {
          setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
        }
      } catch {
        console.error('Error checking payment');
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      }
    };

    checkPaymentStatus();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Vérification du paiement...</h2>
          <p className="text-gray-500 mt-2">Merci de patienter quelques instants</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6 animate-scale-in">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Commande confirmée!</h1>
          <p className="text-gray-500 mt-2">
            Merci pour votre commande{order?.order_number ? ` n°${order.order_number}` : ''}
          </p>
        </div>

        {order && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4">Détails de la commande</h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Heure de retrait</p>
                  <p className="font-semibold">
                    {formatPickupTime(order.pickup_slot, order.pickup_time, order.created_at)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Restaurant</p>
                  <p className="font-semibold">{order.restaurant_name}</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Articles</h3>
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between py-2">
                    <span>{item.quantity}x {item.dish_name}</span>
                    <span className="font-medium">{item.total_price.toFixed(2)} €</span>
                  </div>
                ))}
                <div className="border-t border-gray-100 mt-2 pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-emerald-600">{order.total_amount.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => order && navigate(`/commandes/${order.id}`)}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-full"
            disabled={!order}
            data-testid="view-order-button"
          >
            Suivre ma commande
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            onClick={() => navigate('/restaurants')}
            variant="outline"
            className="flex-1 rounded-full"
          >
            Continuer mes achats
          </Button>
        </div>
      </div>
    </Layout>
  );
}
